import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ChevronDown, ChevronRight, FileText, History, Link2, ListOrdered, Loader2, RefreshCw, Save, Settings2, Trees } from 'lucide-react';

const CONTRACTS_AM_URL = process.env.REACT_APP_CONTRACTS_AM_URL
  || 'https://gjouokpgdwlcdjdenpdt.supabase.co/functions/v1/contracts-am';
const FFPV_FLYER_CONTRACT_KEY = 'ffpv-flyer';
const PLACEHOLDER_PATTERN = /\{\{\s*([^}]+?)\s*\}\}/g;

function extractPlaceholders(html) {
  if (typeof html !== 'string') return [];
  const seen = new Set();
  const result = [];
  let match;
  PLACEHOLDER_PATTERN.lastIndex = 0;
  while ((match = PLACEHOLDER_PATTERN.exec(html)) !== null) {
    const key = String(match[1] || '').trim();
    if (key && !seen.has(key)) {
      seen.add(key);
      result.push(key);
    }
  }
  return result;
}

const FFPV_SYSTEM_FIELDS = {
  OWNER_NAME: { label: 'Eigentümer', desc: 'Kontaktname → Firma → Deal-Titel (automatisch).' },
  ANSPRECHPARTNER_NAME: { label: 'Ansprechpartner — Name', desc: 'Name des Deal-Verantwortlichen (ASSIGNED_USER.NAME, automatisch).' },
  ANSPRECHPARTNER_EMAIL: { label: 'Ansprechpartner — E-Mail', desc: 'E-Mail des Deal-Verantwortlichen (ASSIGNED_USER.EMAIL, automatisch).' },
  ANSPRECHPARTNER_TEL: { label: 'Ansprechpartner — Telefon', desc: 'Telefon des Deal-Verantwortlichen (ASSIGNED_USER.PHONE, automatisch).' },
  NEIGHBOR_GEMARKUNG: { label: 'Nachbar-Gemarkung', desc: 'Gemarkung des Referenzdeals (Feld UF_CRM_1784648299360 im Quell-Deal).' },
  NEIGHBOR_FLUR: { label: 'Nachbar-Flur', desc: 'Flur des Referenzdeals (Feld UF_CRM_1784648299360 im Quell-Deal).' },
  NEIGHBOR_FLURSTUECK: { label: 'Nachbar-Flurstück', desc: 'Flurstück des Referenzdeals (Feld UF_CRM_1784648299360 im Quell-Deal).' },
  PLOT_REF: { label: 'Flurstücksangabe', desc: 'Gemarkung · Flur · Flurstück des Deals (automatisch).' },
  PLOT_IMAGE: { label: 'Luftbild der Fläche', desc: 'Bild aus Deal-Feld UF_CRM_1740525850222 (leer → Bild des Referenzdeals), automatisch eingepasst (cover). Ohne Bild: Platzhalter.' },
  AREA_HA: { label: 'Fläche (ha)', desc: 'Fläche des Deals in ha, deutsch formatiert (z. B. „ca. 12,5 ha").' },
  POWER_MWP: { label: 'Leistung (MWp)', desc: 'Leistung ≈ Fläche in ha × 1,0 MWp.' },
};

const FFPV_FIELD_HINTS = [
  { value: 'DEAL.TITLE', label: 'Deal-Titel' },
  { value: 'DEAL.ID', label: 'Deal-ID' },
  { value: 'CONTACT.NAME,CONTACT.LAST_NAME', label: 'Kontakt Vor- + Nachname' },
  { value: 'COMPANY.TITLE', label: 'Firmenname' },
  { value: 'ASSIGNED_USER.NAME', label: 'Verantwortlicher — Name' },
  { value: 'ASSIGNED_USER.EMAIL', label: 'Verantwortlicher — E-Mail' },
  { value: 'ASSIGNED_USER.PHONE', label: 'Verantwortlicher — Telefon' },
  { value: 'UF_CRM_1752323992', label: 'Gemarkung' },
  { value: 'UF_CRM_1752321437', label: 'Flur' },
  { value: 'UF_CRM_1752324013', label: 'Flurstück' },
  { value: 'UF_CRM_1752312683120', label: 'Fläche (ha)' },
  { value: 'calc(UF_CRM_1752312683120 * 1)', label: 'Berechnung, z. B. Fläche × 1' },
  { value: 'raw:<b>Text</b>', label: 'raw: Text 1:1 (ohne HTML-Maskierung)' },
];

export default function FfpvFlyerBoard({ apiBase = '/api', session = null, canEdit = true }) {
  const accessToken = session?.access_token || null;
  const isAuthenticated = Boolean(accessToken);

  const [templateSource, setTemplateSource] = useState('');
  const [templateHtml, setTemplateHtml] = useState('');
  const [editorHtml, setEditorHtml] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [mappingDraft, setMappingDraft] = useState({});
  const [mappingSavedAt, setMappingSavedAt] = useState(null);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [queueItems, setQueueItems] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [historyEntries, setHistoryEntries] = useState([]);

  const authHeaders = useMemo(() => (
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
  ), [accessToken]);

  const loadQueue = useCallback(async () => {
    if (!isAuthenticated) {
      setQueueItems([]);
      return;
    }
    setQueueLoading(true);
    try {
      const response = await axios.get(apiBase, {
        params: { op: 'ffpv-flyer-pending', _ts: Date.now() },
        headers: authHeaders,
      });
      setQueueItems(response.data?.items || []);
    } catch (queueError) {
      console.error(queueError);
      setQueueItems([]);
    } finally {
      setQueueLoading(false);
    }
  }, [apiBase, authHeaders, isAuthenticated]);

  const loadHistory = useCallback(async () => {
    if (!isAuthenticated) {
      setHistoryEntries([]);
      return;
    }
    try {
      const response = await axios.get(apiBase, {
        params: { op: 'ffpv-flyer-history', _ts: Date.now() },
        headers: authHeaders,
      });
      setHistoryEntries(response.data?.entries || []);
    } catch (historyError) {
      console.error(historyError);
      setHistoryEntries([]);
    }
  }, [apiBase, authHeaders, isAuthenticated]);

  const loadTemplate = useCallback(async () => {
    if (!isAuthenticated) return;
    setTemplateLoading(true);
    try {
      const response = await axios.get(apiBase, {
        params: { op: 'ffpv-flyer-template', _ts: Date.now() },
        headers: authHeaders,
      });
      const data = response.data || {};
      if (data.ok === false) throw new Error(data.error || 'Vorlage konnte nicht geladen werden.');
      const html = String(data.html || '');
      setTemplateHtml(html);
      setEditorHtml(html);
      setTemplateSource(data.source || '');
      setPlaceholders(extractPlaceholders(html));
    } catch (loadError) {
      console.error(loadError);
      toast.error(loadError?.response?.data?.error || loadError?.message || 'Vorlage konnte nicht geladen werden.');
    } finally {
      setTemplateLoading(false);
    }
  }, [apiBase, authHeaders, isAuthenticated]);

  const loadMapping = useCallback(async () => {
    if (!isAuthenticated) {
      setMappingDraft({});
      setMappingSavedAt(null);
      return;
    }
    try {
      const response = await axios.get(CONTRACTS_AM_URL, {
        params: { op: 'mapping', contract_key: FFPV_FLYER_CONTRACT_KEY },
        headers: authHeaders,
      });
      const mapping = response.data?.mapping;
      setMappingDraft(mapping?.fields || {});
      setMappingSavedAt(mapping?.updatedAt || null);
    } catch (mappingError) {
      console.error(mappingError);
      const detail = mappingError?.response?.data?.detail;
      if (detail) toast.error(`Mapping nicht ladbar: ${detail}`);
      setMappingDraft({});
      setMappingSavedAt(null);
    }
  }, [authHeaders, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadTemplate();
      loadMapping();
      loadQueue();
      loadHistory();
    }
  }, [isAuthenticated, loadTemplate, loadMapping, loadQueue, loadHistory]);

  const handleMappingChange = useCallback((placeholder, value) => {
    setMappingDraft((current) => ({ ...current, [placeholder]: value }));
  }, []);

  const handleSaveMapping = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    setMappingSaving(true);
    try {
      await axios.post(CONTRACTS_AM_URL, {
        op: 'mapping',
        contract_key: FFPV_FLYER_CONTRACT_KEY,
        fields: mappingDraft,
      }, { headers: authHeaders });
      setMappingSavedAt(new Date().toISOString());
      toast.success('Mapping gespeichert.');
    } catch (saveError) {
      console.error(saveError);
      toast.error(saveError?.response?.data?.detail || saveError?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setMappingSaving(false);
    }
  }, [authHeaders, isAuthenticated, mappingDraft]);

  const handleSaveTemplate = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (!editorHtml.trim()) {
      toast.error('Die Vorlage darf nicht leer sein.');
      return;
    }
    setTemplateSaving(true);
    try {
      const response = await fetch(apiBase, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'ffpv-flyer-template-save', html: editorHtml }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error(data.error || data.detail || `Fehler (${response.status}).`);
      }
      setTemplateHtml(editorHtml);
      setTemplateSource('saved');
      setPlaceholders(extractPlaceholders(editorHtml));
      toast.success('Vorlage gespeichert. Wirkt auf alle künftigen Flyer.');
    } catch (saveError) {
      console.error(saveError);
      toast.error(saveError?.message || 'Vorlage konnte nicht gespeichert werden.');
    } finally {
      setTemplateSaving(false);
    }
  }, [apiBase, authHeaders, editorHtml, isAuthenticated]);

  const handleRevertTemplate = useCallback(() => {
    setEditorHtml(templateHtml);
    setPlaceholders(extractPlaceholders(templateHtml));
  }, [templateHtml]);

  const templateDirty = editorHtml !== templateHtml;

  const extraMappingKeys = useMemo(() => {
    const inTemplate = new Set(placeholders);
    return Object.keys(mappingDraft || {})
      .filter((key) => key && String(mappingDraft[key] || '').trim() && !inTemplate.has(key))
      .sort((a, b) => a.localeCompare(b, 'de'));
  }, [mappingDraft, placeholders]);

  return (
    <div style={{ flex: 1, minHeight: 0, background: '#F8FAFC', overflowY: 'auto' }}>
      <div style={{ margin: '16px 20px', border: '1px solid #E2E8F0', borderRadius: 12, background: '#FFFFFF', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Trees size={18} color="#122A5C" />
          <div style={{ flex: 1 }}>
            <div style={{ color: '#122A5C', fontSize: 14, fontWeight: 700 }}>FFPV Flyer — Konfiguration &amp; Vorschau</div>
            <div style={{ color: '#64748B', fontSize: 12 }}>
              Feld-Mapping und Template-Vorschau. Die Flyer-Erstellung läuft automatisch über die Bitrix-Automatisierung.
            </div>
          </div>
          <span style={{ padding: '4px 9px', borderRadius: 999, background: templateSource === 'saved' ? '#ECFDF5' : '#F1F5F9', color: templateSource === 'saved' ? '#047857' : '#475569', fontSize: 11, fontWeight: 800 }}>
            {templateSource === 'saved' ? 'Gespeicherte Vorlage' : templateSource === 'default' ? 'Standardvorlage' : '—'}
          </span>
          <button
            onClick={() => { loadTemplate(); loadMapping(); loadQueue(); loadHistory(); }}
            disabled={templateLoading || !isAuthenticated}
            title="Vorlage + Mapping neu laden"
            style={{ padding: '7px 10px', border: '1px solid #CBD5E1', borderRadius: 8, background: '#FFFFFF', color: '#0F172A', fontSize: 12, fontWeight: 700, cursor: templateLoading || !isAuthenticated ? 'not-allowed' : 'pointer', opacity: templateLoading || !isAuthenticated ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {templateLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Aktualisieren
          </button>
        </div>

        {/* Main: zwei Spalten — Mapping links, Vorschau rechts */}
        {templateLoading && !templateHtml ? (
          <div style={{ padding: '40px 16px', textAlign: 'center', color: '#64748B', fontSize: 13 }}>
            <Loader2 size={22} className="animate-spin" style={{ display: 'inline-block', marginBottom: 8 }} />
            <div>Lade Vorlage und Mapping…</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', minHeight: 500 }}>
            {/* LINKS: Feld-Mapping */}
            <div style={{ borderRight: '1px solid #E2E8F0', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '12px 14px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Link2 size={14} color="#122A5C" />
                <span style={{ flex: 1, color: '#122A5C', fontSize: 13, fontWeight: 900 }}>Feld-Mapping ({placeholders.length} Platzhalter)</span>
                <button
                  onClick={handleSaveMapping}
                  disabled={mappingSaving || !isAuthenticated || !canEdit}
                  title={!canEdit ? 'Keine Bearbeitungsrechte' : 'Mapping speichern'}
                  style={{ padding: '6px 10px', border: '1px solid #122A5C', borderRadius: 6, background: mappingSaving || !isAuthenticated || !canEdit ? '#CBD5E1' : '#122A5C', color: '#FFFFFF', fontSize: 11, fontWeight: 700, cursor: mappingSaving || !isAuthenticated || !canEdit ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <Save size={11} />
                  {mappingSaving ? '…' : 'Speichern'}
                </button>
              </div>

              <div style={{ padding: '8px 14px', borderBottom: '1px solid #F1F5F9', background: '#FAFAF9' }}>
                <div style={{ color: '#64748B', fontSize: 11, lineHeight: 1.45 }}>
                  Schreibe <code style={{ fontFamily: 'monospace', fontSize: 10.5 }}>{'{{Platzhalter}}'}</code> in die Vorlage und lege hier fest, welches Bitrix-Feld hineingemappt wird.
                  Systemfelder funktionieren auch ohne Mapping; ein Eintrag überschreibt sie.
                </div>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
                {placeholders.length === 0 ? (
                  <div style={{ padding: '16px 14px', color: '#94A3B8', fontSize: 12 }}>Keine Platzhalter in der Vorlage gefunden.</div>
                ) : placeholders.map((placeholder) => {
                  const isSystem = Object.prototype.hasOwnProperty.call(FFPV_SYSTEM_FIELDS, placeholder);
                  return (
                    <div key={placeholder} style={{ padding: '8px 14px', borderBottom: '1px solid #F8FAFC' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                        <code style={{ fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700, color: '#0F172A' }}>{`{{${placeholder}}}`}</code>
                        {isSystem ? (
                          <span style={{ padding: '1px 7px', borderRadius: 999, background: '#EFF6FF', color: '#0369A1', fontSize: 9.5, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Systemfeld</span>
                        ) : null}
                      </div>
                      {isSystem ? (
                        <div style={{ color: '#64748B', fontSize: 10.5, lineHeight: 1.35, marginBottom: 4 }}>{FFPV_SYSTEM_FIELDS[placeholder].desc}</div>
                      ) : null}
                      <input
                        list="ffpv-field-hints"
                        value={mappingDraft[placeholder] || ''}
                        onChange={(event) => handleMappingChange(placeholder, event.target.value)}
                        placeholder={isSystem ? 'leer = Systemfeld, oder z. B. DEAL.TITLE' : 'z. B. DEAL.TITLE, UF_CRM_…'}
                        disabled={!isAuthenticated || !canEdit}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 11.5, fontFamily: 'monospace', color: '#0F172A', boxSizing: 'border-box' }}
                      />
                    </div>
                  );
                })}

                {extraMappingKeys.length > 0 ? (
                  <div style={{ margin: '8px 0 0', borderTop: '1px dashed #CBD5E1', background: '#FAFAF9', padding: '8px 14px' }}>
                    <div style={{ color: '#92400E', fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                      Weitere gespeicherte Zuordnungen ({extraMappingKeys.length})
                    </div>
                    <div style={{ color: '#78716C', fontSize: 10.5, lineHeight: 1.4, marginBottom: 6 }}>
                      Platzhalter, die aktuell nicht in der Vorlage stehen. Bleiben gespeichert, bis du sie leerst.
                    </div>
                    {extraMappingKeys.map((key) => (
                      <div key={key} style={{ padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <label style={{ color: '#0F172A', fontSize: 11, fontWeight: 700, fontFamily: 'monospace' }}>{`{{${key}}}`}</label>
                        <input
                          list="ffpv-field-hints"
                          value={mappingDraft[key] || ''}
                          onChange={(event) => handleMappingChange(key, event.target.value)}
                          disabled={!isAuthenticated || !canEdit}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #E7E5E4', borderRadius: 6, fontSize: 11, fontFamily: 'monospace', color: '#475569', boxSizing: 'border-box' }}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}

                <datalist id="ffpv-field-hints">
                  {FFPV_FIELD_HINTS.map((hint) => (<option key={hint.value} value={hint.value}>{hint.label}</option>))}
                </datalist>
              </div>

              <div style={{ padding: '8px 14px', borderTop: '1px solid #E2E8F0', background: '#F8FAFC', color: '#475569', fontSize: 11 }}>
                {mappingSavedAt ? `Mapping gespeichert: ${new Date(mappingSavedAt).toLocaleString('de-DE')}` : 'Noch kein Mapping gespeichert.'}
              </div>
            </div>

            {/* RECHTS: Template-Vorschau */}
            <div style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', background: '#CBD5E1' }}>
              <div style={{ padding: '12px 14px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={14} color="#122A5C" />
                <span style={{ flex: 1, color: '#122A5C', fontSize: 13, fontWeight: 900 }}>Flyer-Vorschau</span>
                <span style={{ color: '#64748B', fontSize: 10.5 }}>Platzhalter werden bei der Generierung durch Deal-Daten ersetzt</span>
              </div>

              {templateHtml ? (
                <div style={{ flex: 1, background: '#CBD5E1', padding: 0, overflow: 'hidden' }}>
                  <iframe
                    srcDoc={templateHtml}
                    title="FFPV Flyer Vorschau"
                    sandbox="allow-same-origin"
                    style={{ width: '100%', height: '100%', minHeight: 700, border: 'none', background: '#CBD5E1' }}
                  />
                </div>
              ) : (
                <div style={{ padding: '40px 14px', textAlign: 'center', color: '#94A3B8', fontSize: 12 }}>
                  {isAuthenticated ? 'Keine Vorlage geladen.' : 'Bitte einloggen, um die Vorlage zu laden.'}
                </div>
              )}

            </div>
          </div>
        )}

        {/* Warteschlange + Historie */}
        <div style={{ borderTop: '1px solid #E2E8F0', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          {/* LINKS: Warteschlange */}
          <div style={{ borderRight: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 8 }}>
              <ListOrdered size={15} color="#122A5C" />
              <span style={{ color: '#122A5C', fontSize: 13, fontWeight: 900 }}>Warteschlange ({queueItems.length})</span>
              {queueLoading ? <Loader2 size={13} className="animate-spin" color="#94A3B8" /> : null}
            </div>
            <div style={{ padding: '6px 16px 8px', color: '#64748B', fontSize: 11, lineHeight: 1.4 }}>
              Deals in Phase „Flyer benötigt" — werden automatisch nacheinander abgearbeitet.
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 8px' }}>
              {queueItems.length === 0 ? (
                <div style={{ padding: '14px 16px', color: '#94A3B8', fontSize: 12 }}>Keine Deals in der Warteschlange.</div>
              ) : queueItems.map((item) => {
                const sub = item.responsibleName || item.ownerName || '';
                return (
                  <div key={item.dealId} style={{ padding: '8px 16px', borderBottom: '1px solid #F8FAFC' }}>
                    <div style={{ color: '#0F172A', fontSize: 12.5, fontWeight: 700 }}>
                      #{item.dealId} · {item.title || 'Ohne Titel'}
                    </div>
                    {sub ? (
                      <div style={{ color: '#64748B', fontSize: 11, marginTop: 2 }}>{sub}</div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          {/* RECHTS: Historie */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 8 }}>
              <History size={15} color="#122A5C" />
              <span style={{ color: '#122A5C', fontSize: 13, fontWeight: 900 }}>Historie (letzte 10)</span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0 8px' }}>
              {historyEntries.length === 0 ? (
                <div style={{ padding: '14px 16px', color: '#94A3B8', fontSize: 12 }}>Noch keine Flyer erstellt.</div>
              ) : historyEntries.map((entry, index) => {
                const isSuccess = entry.status === 'success';
                return (
                  <div key={`${entry.dealId}-${entry.at}-${index}`} style={{ padding: '8px 16px', borderBottom: '1px solid #F8FAFC' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ padding: '1px 8px', borderRadius: 999, background: isSuccess ? '#ECFDF5' : '#FEF2F2', color: isSuccess ? '#047857' : '#B91C1C', fontSize: 10, fontWeight: 800 }}>
                        {isSuccess ? 'Erfolg' : 'Fehler'}
                      </span>
                      <span style={{ color: '#0F172A', fontSize: 12, fontWeight: 700 }}>
                        #{entry.dealId}{entry.dealTitle ? ` · ${entry.dealTitle}` : ''}
                      </span>
                      <span style={{ padding: '1px 7px', borderRadius: 999, background: '#F1F5F9', color: '#475569', fontSize: 9.5, fontWeight: 800 }}>
                        {entry.trigger === 'webhook' ? 'Auto' : 'Manuell'}
                      </span>
                    </div>
                    <div style={{ color: '#94A3B8', fontSize: 10.5, marginTop: 3 }}>
                      {entry.at ? new Date(entry.at).toLocaleString('de-DE') : ''}
                    </div>
                    {isSuccess && entry.filename ? (
                      <div style={{ color: '#64748B', fontSize: 10.5, marginTop: 2, wordBreak: 'break-all' }}>{entry.filename}</div>
                    ) : null}
                    {!isSuccess && entry.error ? (
                      <div style={{ color: '#B91C1C', fontSize: 10.5, marginTop: 2 }}>{entry.error}</div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Vorlage bearbeiten (aufklappbar) */}
        <div style={{ borderTop: '1px solid #E2E8F0' }}>
          <button
            onClick={() => setTemplateEditorOpen((open) => !open)}
            style={{ width: '100%', padding: '10px 16px', border: 'none', background: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', textAlign: 'left' }}
          >
            {templateEditorOpen ? <ChevronDown size={15} color="#475569" /> : <ChevronRight size={15} color="#475569" />}
            <Settings2 size={14} color="#475569" />
            <span style={{ flex: 1, color: '#475569', fontSize: 12, fontWeight: 800 }}>Vorlage bearbeiten (HTML)</span>
            {templateDirty ? (
              <span style={{ padding: '2px 8px', borderRadius: 999, background: '#FEF3C7', color: '#92400E', fontSize: 10, fontWeight: 800 }}>Ungespeichert</span>
            ) : null}
          </button>

          {templateEditorOpen ? (
            <div style={{ padding: '10px 16px 16px', display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#B45309', fontSize: 11, fontWeight: 700 }}>
                  <AlertTriangle size={12} />
                  Änderungen wirken auf alle künftigen Flyer.
                </div>
                <div style={{ flex: 1 }} />
                <button
                  onClick={handleRevertTemplate}
                  disabled={!templateDirty}
                  style={{ padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, background: '#FFFFFF', color: '#0F172A', fontSize: 11, fontWeight: 700, cursor: templateDirty ? 'pointer' : 'not-allowed', opacity: templateDirty ? 1 : 0.5 }}
                >
                  Verwerfen
                </button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={templateSaving || !isAuthenticated || !canEdit}
                  style={{ padding: '6px 10px', border: '1px solid #122A5C', borderRadius: 6, background: templateSaving || !isAuthenticated || !canEdit ? '#CBD5E1' : '#122A5C', color: '#FFFFFF', fontSize: 11, fontWeight: 700, cursor: templateSaving || !isAuthenticated || !canEdit ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <Save size={11} />
                  {templateSaving ? '…' : 'Vorlage speichern'}
                </button>
              </div>
              <textarea
                value={editorHtml}
                onChange={(event) => {
                  setEditorHtml(event.target.value);
                  setPlaceholders(extractPlaceholders(event.target.value));
                }}
                disabled={!isAuthenticated || !canEdit}
                spellCheck={false}
                wrap="off"
                style={{ width: '100%', minHeight: 220, resize: 'vertical', padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 6, background: '#0F172A', color: '#E2E8F0', fontSize: 12, fontFamily: 'monospace', lineHeight: 1.5, whiteSpace: 'pre', overflowX: 'auto', boxSizing: 'border-box' }}
              />
              <div style={{ color: '#64748B', fontSize: 11 }}>
                Quelle: <strong>{templateSource === 'saved' ? 'gespeicherte Vorlage' : templateSource === 'default' ? 'Standardvorlage' : '—'}</strong>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
